import PropTypes from 'prop-types';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Chip,
  Dialog,
  InputBase,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
// hooks / utils
import useAuth from '../../hooks/useAuth';
import { useCampus } from '../../hooks/useCampus';
import { useRefetchTrigger } from '../../hooks/useRefetchTrigger';
import axios from '../../utils/axios';
import { formatMoney } from '../../utils/currency';
import { isItemVisible } from '../../guards/checkNavAccess';
import { adminNavConfig } from '../../layouts/dashboard/navbar/NavConfig';
import { PATH_ADMIN } from '../../routes/paths';
// components
import Iconify from '../Iconify';
import {
  DEX_GATE,
  FILTERS,
  QUICK_ACTIONS,
  TYPE_META,
  fuzzyScore,
  looksLikeQuestion,
  resultTarget,
} from './spotlightConfig';

// ----------------------------------------------------------------------

const SEARCH_URL = '/api/v1/accounts/admin/search/';
const DEBOUNCE_MS = 180;
const RECENT_MAX = 8;
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');
export const MOD_KEY = IS_MAC ? '⌘' : 'Ctrl';

const recentKey = (user) => `deskos.spotlight.recent.${user?.id || 'anon'}`;

function readRecent(user) {
  try {
    const raw = localStorage.getItem(recentKey(user));
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
}

function pushRecent(user, entry) {
  try {
    const key = `${entry.type}:${entry.id}`;
    const next = [entry, ...readRecent(user).filter((r) => `${r.type}:${r.id}` !== key)].slice(0, RECENT_MAX);
    localStorage.setItem(recentKey(user), JSON.stringify(next));
  } catch (e) {
    // Private window or blocked storage: recents are a convenience only.
  }
}

function clearRecent(user) {
  try {
    localStorage.removeItem(recentKey(user));
  } catch (e) {
    // ignore
  }
}

const STATUS_COLORS = [
  [/cancel|lost|blacklist|overdue|closed_lost|rejected|expired/i, 'error'],
  [/paid$|^paid|active|won|resolved|completed|confirmed|available|accepted/i, 'success'],
  [/partial|progress|notice|hold|draft|sent/i, 'info'],
  [/pending|open|new|due|unpaid/i, 'warning'],
];

const statusColor = (status) => {
  const s = String(status || '');
  const hit = STATUS_COLORS.find(([re]) => re.test(s));
  return hit ? hit[1] : 'default';
};

const prettyStatus = (s) =>
  String(s || '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());

const formatWhen = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
};

/** Wrap every query token found in `text` in a <mark>. */
function Highlight({ text, tokens }) {
  if (!text) return null;
  const clean = tokens.filter((t) => t.length > 0).map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (!clean.length) return <>{text}</>;
  const parts = String(text).split(new RegExp(`(${clean.join('|')})`, 'ig'));
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <Box
            key={i}
            component="mark"
            sx={{ bgcolor: 'transparent', color: 'primary.main', fontWeight: 700, p: 0 }}
          >
            {part}
          </Box>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        )
      )}
    </>
  );
}

Highlight.propTypes = { text: PropTypes.node, tokens: PropTypes.array };

export function Kbd({ children, sx }) {
  return (
    <Box
      component="kbd"
      sx={{
        px: 0.75,
        minWidth: 22,
        height: 22,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 0.75,
        fontSize: 11,
        fontFamily: 'inherit',
        fontWeight: 600,
        color: 'text.secondary',
        bgcolor: (t) => alpha(t.palette.grey[500], 0.12),
        border: (t) => `1px solid ${alpha(t.palette.grey[500], 0.24)}`,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

Kbd.propTypes = { children: PropTypes.node, sx: PropTypes.object };

function StatusChip({ status, customColor }) {
  const theme = useTheme();
  const key = statusColor(status);
  const color = customColor || (key === 'default' ? theme.palette.grey[600] : theme.palette[key].main);
  return (
    <Box
      component="span"
      sx={{
        px: 0.75,
        height: 20,
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 0.75,
        fontSize: 11,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        color: theme.palette.mode === 'light' && !customColor && key !== 'default' ? theme.palette[key].dark : color,
        bgcolor: alpha(color, 0.14),
      }}
    >
      {prettyStatus(status)}
    </Box>
  );
}

StatusChip.propTypes = { status: PropTypes.string, customColor: PropTypes.string };

// ----------------------------------------------------------------------

SpotlightDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  initialQuery: PropTypes.string,
};

export default function SpotlightDialog({ open, onClose, initialQuery = '' }) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user, hasPermission } = useAuth();
  const { activeCampus, allCampuses, setActiveCampus } = useCampus();
  const { triggerRefetch } = useRefetchTrigger();

  const [query, setQuery] = useState(initialQuery);
  const [filter, setFilter] = useState(null);
  const [campusOnly, setCampusOnly] = useState(false);
  const [results, setResults] = useState({ sections: [], top_hit: null, available: null });
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [recent, setRecent] = useState([]);

  const inputRef = useRef(null);
  const requestSeq = useRef(0);
  const rowRefs = useRef({});

  const ctx = useMemo(() => ({ user, hasPermission }), [user, hasPermission]);
  const multiCampus = (allCampuses?.length || 0) > 1;
  const dexAvailable = useMemo(() => isItemVisible(DEX_GATE, ctx), [ctx]);
  const orgCurrency = user?.orgCurrency;

  // Fresh state every time the palette opens.
  useEffect(() => {
    if (!open) return;
    setQuery(initialQuery || '');
    setFilter(null);
    setActiveIndex(0);
    setRecent(readRecent(user));
    // Focus after the enter transition has attached the input.
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const q = query.trim();
  const tokens = useMemo(() => q.split(/\s+/).filter(Boolean), [q]);

  // ------------------------------------------------------------ server search
  useEffect(() => {
    if (!open) return undefined;
    if (q.length < 2) {
      requestSeq.current += 1;
      setResults((r) => ({ sections: [], top_hit: null, available: r.available }));
      setLoading(false);
      return undefined;
    }
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(SEARCH_URL, {
          params: {
            q,
            types: filter || undefined,
            limit: filter ? 15 : 5,
            campus_only: campusOnly ? 1 : undefined,
          },
        });
        if (seq === requestSeq.current) setResults(data);
      } catch (e) {
        if (seq === requestSeq.current) setResults({ sections: [], top_hit: null, available: null });
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [open, q, filter, campusOnly]);

  // ------------------------------------------------------------ client lists
  const pages = useMemo(() => {
    const out = [];
    adminNavConfig.forEach((group) => {
      group.items.forEach((item) => {
        if (!isItemVisible(item, ctx)) return;
        const kids = (item.children || []).filter((c) => isItemVisible(c, ctx) && c.path);
        if (item.path && !kids.some((c) => c.path === item.path)) {
          out.push({ id: item.path, title: item.title, crumb: group.subheader, path: item.path });
        }
        kids.forEach((c) =>
          out.push({ id: c.path, title: c.title, crumb: item.title, path: c.path, parent: item.title })
        );
      });
    });
    return out;
  }, [ctx]);

  const actions = useMemo(() => QUICK_ACTIONS.filter((a) => isItemVisible({ ...a.gate, path: a.path }, ctx)), [ctx]);

  // ------------------------------------------------------------ groups & rows
  const groups = useMemo(() => {
    const out = [];
    const dexRow = dexAvailable && q ? [{ kind: 'dex', key: 'dex', title: q }] : [];

    if (!q) {
      if (recent.length) {
        out.push({ key: 'recent', title: 'Recent', clearable: true, rows: recent.map((r) => ({ kind: r.kind || 'record', key: `recent:${r.type}:${r.id}`, item: r })) });
      }
      if (actions.length) {
        out.push({ key: 'actions', title: 'Quick actions', rows: actions.map((a) => ({ kind: 'action', key: `action:${a.id}`, item: a })) });
      }
      if (dexAvailable) {
        out.push({ key: 'dex', title: 'Assistant', rows: [{ kind: 'dex', key: 'dex-open', title: '' }] });
      }
      return out;
    }

    const question = looksLikeQuestion(q);
    if (question && dexRow.length) out.push({ key: 'dex-first', title: 'Ask Dex', rows: dexRow });

    const sections = results.sections || [];
    const top = !filter && results.top_hit ? results.top_hit : null;
    if (top) out.push({ key: 'top', title: 'Top hit', rows: [{ kind: 'record', key: `top:${top.type}:${top.id}`, item: top, big: true }] });

    if (!filter) {
      const scoredPages = pages
        .map((p) => ({ p, s: Math.max(fuzzyScore(q, p.title), fuzzyScore(q, `${p.crumb || ''} ${p.title}`) - 10) }))
        .filter((x) => x.s >= 40)
        .sort((a, b) => b.s - a.s)
        .slice(0, 4)
        .map(({ p }) => ({ kind: 'page', key: `page:${p.id}`, item: p }));
      const scoredActions = actions
        .map((a) => ({ a, s: fuzzyScore(q, a.title, a.keywords) }))
        .filter((x) => x.s >= 40)
        .sort((x, y) => y.s - x.s)
        .slice(0, 3)
        .map(({ a }) => ({ kind: 'action', key: `action:${a.id}`, item: a }));
      const campuses = multiCampus
        ? allCampuses
            .map((c) => ({ c, s: fuzzyScore(q, c.name) }))
            .filter((x) => x.s >= 50 && x.c.id !== activeCampus?.id)
            .slice(0, 3)
            .map(({ c }) => ({ kind: 'campus', key: `campus:${c.id}`, item: c }))
        : [];
      if (scoredPages.length || scoredActions.length) {
        out.push({ key: 'go', title: 'Pages & actions', rows: [...scoredActions, ...scoredPages] });
      }
      if (campuses.length) out.push({ key: 'campuses', title: 'Switch campus', rows: campuses });
    }

    sections.forEach((s) => {
      const rows = s.items
        .filter((it) => !(top && it.type === top.type && it.id === top.id))
        .map((it) => ({ kind: 'record', key: `${s.key}:${it.type}:${it.id}`, item: it }));
      if (rows.length) out.push({ key: s.key, title: s.label, rows });
    });

    if (!question && dexRow.length) out.push({ key: 'dex-last', title: 'Ask Dex', rows: dexRow });
    return out;
  }, [q, recent, actions, dexAvailable, results, filter, pages, multiCampus, allCampuses, activeCampus]);

  const flatRows = useMemo(() => groups.flatMap((g) => g.rows), [groups]);

  useEffect(() => {
    setActiveIndex(0);
  }, [q, filter, results]);

  useEffect(() => {
    rowRefs.current[activeIndex]?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  // ------------------------------------------------------------ actions
  const switchCampus = useCallback(
    (campusId, quiet) => {
      if (!campusId || campusId === activeCampus?.id) return;
      const campus = (allCampuses || []).find((c) => c.id === campusId);
      if (!campus) return;
      setActiveCampus(campus);
      triggerRefetch();
      if (!quiet) enqueueSnackbar(`Switched to ${campus.name}`, { variant: 'info' });
    },
    [activeCampus, allCampuses, setActiveCampus, triggerRefetch, enqueueSnackbar]
  );

  const askDex = useCallback(
    (text) => {
      onClose();
      navigate(PATH_ADMIN.dex, text ? { state: { dexPrompt: text } } : undefined);
    },
    [navigate, onClose]
  );

  const select = useCallback(
    (row) => {
      if (!row) return;
      if (row.kind === 'dex') {
        askDex(row.title);
        return;
      }
      if (row.kind === 'campus') {
        switchCampus(row.item.id);
        onClose();
        return;
      }
      if (row.kind === 'page' || row.kind === 'action') {
        const it = row.item;
        pushRecent(user, { kind: row.kind, type: row.kind === 'page' ? 'page' : 'action', id: it.id, title: it.title, crumb: it.crumb, subtitle: it.subtitle, path: it.path, icon: it.icon });
        onClose();
        navigate(it.path);
        return;
      }
      // Record: open it where it lives, switching campus first if it lives elsewhere.
      const it = row.item;
      const target = resultTarget(it);
      if (!target) return;
      if (multiCampus && it.campus_id && it.campus_id !== activeCampus?.id) {
        const known = allCampuses.some((c) => c.id === it.campus_id);
        if (known) {
          switchCampus(it.campus_id, true);
          enqueueSnackbar(`Opened in ${it.campus_name || 'its campus'}`, { variant: 'info' });
        }
      }
      pushRecent(user, { kind: 'record', ...it });
      onClose();
      navigate(target.path, target.state ? { state: target.state } : undefined);
    },
    [askDex, switchCampus, onClose, navigate, user, multiCampus, activeCampus, allCampuses, enqueueSnackbar]
  );

  const availableFilters = useMemo(
    () => FILTERS.filter((f) => !results.available || results.available.includes(f.key)),
    [results.available]
  );

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (flatRows.length ? (i + 1) % flatRows.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (flatRows.length ? (i - 1 + flatRows.length) % flatRows.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if ((e.metaKey || e.ctrlKey) && dexAvailable) {
        askDex(q);
      } else {
        select(flatRows[activeIndex]);
      }
    } else if (e.key === 'Tab' && q) {
      // Tab walks the type filters: All -> Members -> Companies -> ...
      e.preventDefault();
      const keys = [null, ...availableFilters.map((f) => f.key)];
      const at = keys.indexOf(filter);
      const next = e.shiftKey ? (at - 1 + keys.length) % keys.length : (at + 1) % keys.length;
      setFilter(keys[next]);
    } else if (e.key === 'Backspace' && !query && filter) {
      setFilter(null);
    }
  };

  // ------------------------------------------------------------ render
  const paletteColor = (key) => {
    if (key === 'grey') return theme.palette.grey[600];
    return theme.palette[key]?.main || theme.palette.primary.main;
  };

  const renderLead = (meta, row) => {
    const color = paletteColor(meta.color);
    if (row.kind === 'record' && row.item.avatar) {
      return <Avatar src={row.item.avatar} alt={row.item.title} sx={{ width: 36, height: 36 }} />;
    }
    // Actions carry their own icon (also when replayed from Recent).
    const own = typeof row.item?.icon === 'string' ? row.item.icon : null;
    const icon = (row.kind === 'action' || row.item?.type === 'action') && own ? own : meta.icon;
    return (
      <Box
        sx={{
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color,
          bgcolor: alpha(color, 0.12),
        }}
      >
        <Iconify icon={icon} width={20} />
      </Box>
    );
  };

  const renderRow = (row, index) => {
    const active = index === activeIndex;
    let meta;
    let title;
    let subtitle;
    let right = null;

    if (row.kind === 'dex') {
      meta = TYPE_META.dex;
      title = row.title ? (
        <>
          Ask Dex: <Box component="span" sx={{ fontWeight: 700 }}>“{row.title}”</Box>
        </>
      ) : (
        'Ask Dex anything'
      );
      subtitle = row.title
        ? 'Hand this to the AI assistant, it can read across your whole workspace'
        : 'Occupancy, collections, renewals, who is overdue, anything the search cannot answer';
      right = dexAvailable && row.title ? <Kbd>{MOD_KEY} ↵</Kbd> : null;
    } else if (row.kind === 'campus') {
      meta = TYPE_META.campus;
      title = <Highlight text={row.item.name} tokens={tokens} />;
      subtitle = 'Switch the active campus';
    } else if (row.kind === 'page') {
      meta = TYPE_META.page;
      title = <Highlight text={row.item.title} tokens={tokens} />;
      subtitle = row.item.crumb;
    } else if (row.kind === 'action') {
      meta = TYPE_META.action;
      title = <Highlight text={row.item.title} tokens={tokens} />;
      subtitle = row.item.subtitle;
    } else {
      const it = row.item;
      meta = TYPE_META[it.type] || TYPE_META.page;
      if (it.type === 'page' || it.type === 'action') {
        // A page or action out of the recents list.
        title = it.title;
        subtitle = it.subtitle;
      } else {
        title = <Highlight text={it.title} tokens={tokens} />;
        const when = it.type === 'booking' ? formatWhen(it.start) : null;
        subtitle = <Highlight text={[when, it.subtitle].filter(Boolean).join(' · ')} tokens={tokens} />;
        right = (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
            {it.amount != null && (
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', display: { xs: 'none', sm: 'block' } }}>
                {formatMoney(it.amount, it.currency || orgCurrency, 0)}
              </Typography>
            )}
            {it.status && (
              <StatusChip status={it.status} customColor={it.status_color} />
            )}
            {multiCampus && it.campus_name && (
              <Tooltip title={it.campus_id === activeCampus?.id ? 'Active campus' : 'Opens in this campus'}>
                <Stack direction="row" alignItems="center" spacing={0.25} sx={{ color: 'text.disabled', display: { xs: 'none', sm: 'flex' } }}>
                  <Iconify icon="solar:map-point-linear" width={13} />
                  <Typography variant="caption" noWrap sx={{ maxWidth: 110 }}>
                    {it.campus_name}
                  </Typography>
                </Stack>
              </Tooltip>
            )}
          </Stack>
        );
      }
    }

    return (
      <Box
        key={row.key}
        id={`spotlight-row-${index}`}
        role="option"
        aria-selected={active}
        ref={(el) => {
          rowRefs.current[index] = el;
        }}
        onMouseMove={() => !active && setActiveIndex(index)}
        onClick={() => select(row)}
        sx={{
          mx: 1,
          px: 1.25,
          py: row.big ? 1.25 : 0.9,
          borderRadius: 1.25,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          position: 'relative',
          bgcolor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
          ...(row.kind === 'dex' && {
            bgcolor: active ? alpha(theme.palette.secondary.main, 0.12) : alpha(theme.palette.secondary.main, 0.04),
          }),
        }}
      >
        {renderLead(meta, row)}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant={row.big ? 'subtitle1' : 'subtitle2'} noWrap sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" component="div" noWrap sx={{ color: 'text.secondary' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {right}
        {row.kind === 'record' && (
          <Typography variant="caption" sx={{ color: 'text.disabled', flexShrink: 0, width: 78, textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
            {meta.label}
          </Typography>
        )}
        {active && (
          <Iconify icon="solar:alt-arrow-right-linear" width={16} sx={{ color: 'text.disabled', flexShrink: 0 }} />
        )}
      </Box>
    );
  };

  let rowIndex = -1;
  const noResults = q.length >= 2 && !loading && !(results.sections || []).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isPhone}
      maxWidth={false}
      TransitionProps={{ timeout: 120 }}
      PaperProps={{
        sx: {
          ...(isPhone
            ? {}
            : {
                position: 'fixed',
                top: '9vh',
                m: 0,
                width: 'calc(100% - 32px)',
                maxWidth: 720,
                maxHeight: '78vh',
                borderRadius: 2.5,
              }),
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: alpha(theme.palette.background.paper, 0.96),
          backdropFilter: 'blur(18px) saturate(160%)',
          boxShadow: `0 24px 80px -12px ${alpha(theme.palette.grey[900], 0.35)}`,
          border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
        },
      }}
      BackdropProps={{ sx: { bgcolor: alpha(theme.palette.grey[900], 0.32), backdropFilter: 'blur(2px)' } }}
    >
      {/* Search input */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2.5, pt: isPhone ? 'calc(env(safe-area-inset-top) + 12px)' : 2, pb: 1.5 }}>
        <Iconify icon="solar:magnifer-linear" width={22} sx={{ color: 'text.secondary', flexShrink: 0 }} />
        {filter && (
          <Chip
            size="small"
            color="primary"
            label={FILTERS.find((f) => f.key === filter)?.label}
            onDelete={() => setFilter(null)}
            sx={{ flexShrink: 0, fontWeight: 600 }}
          />
        )}
        <InputBase
          inputRef={inputRef}
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            dexAvailable
              ? 'Search members, companies, invoices, leads, rooms… or ask Dex'
              : 'Search members, companies, invoices, leads, rooms…'
          }
          inputProps={{
            'aria-label': 'Search DeskOS',
            role: 'combobox',
            'aria-expanded': true,
            'aria-controls': 'spotlight-listbox',
            'aria-activedescendant': flatRows.length ? `spotlight-row-${activeIndex}` : undefined,
            autoComplete: 'off',
            spellCheck: false,
          }}
          sx={{ fontSize: 18, fontWeight: 500, '& input::placeholder': { color: 'text.disabled', opacity: 1 } }}
        />
        {isPhone ? (
          <Typography variant="subtitle2" color="primary" onClick={onClose} sx={{ cursor: 'pointer', flexShrink: 0 }}>
            Cancel
          </Typography>
        ) : (
          <Kbd sx={{ cursor: 'pointer' }}>
            <span onClick={onClose} role="presentation">Esc</span>
          </Kbd>
        )}
      </Stack>

      {/* Filters + scope */}
      {q.length >= 2 && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{ px: 2.5, pb: 1.25, overflowX: 'auto', '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}
        >
          <Chip
            size="small"
            label="All"
            variant={filter ? 'outlined' : 'filled'}
            color={filter ? 'default' : 'primary'}
            onClick={() => setFilter(null)}
            sx={{ fontWeight: 600 }}
          />
          {availableFilters.map((f) => (
            <Chip
              key={f.key}
              size="small"
              label={f.label}
              variant={filter === f.key ? 'filled' : 'outlined'}
              color={filter === f.key ? 'primary' : 'default'}
              onClick={() => setFilter(filter === f.key ? null : f.key)}
              sx={{ fontWeight: 600, flexShrink: 0 }}
            />
          ))}
          {multiCampus && (
            <>
              <Box sx={{ flexGrow: 1, minWidth: 8 }} />
              <Chip
                size="small"
                icon={<Iconify icon="solar:map-point-linear" width={14} />}
                label={campusOnly ? activeCampus?.name || 'This campus' : 'All campuses'}
                variant="outlined"
                onClick={() => setCampusOnly((v) => !v)}
                sx={{ fontWeight: 600, flexShrink: 0 }}
              />
            </>
          )}
        </Stack>
      )}

      <Box sx={{ height: 2 }}>{loading && <LinearProgress sx={{ height: 2 }} />}</Box>
      <Box sx={{ borderTop: `1px solid ${alpha(theme.palette.grey[500], 0.12)}` }} />

      {/* Results */}
      <Box id="spotlight-listbox" role="listbox" sx={{ flex: 1, overflowY: 'auto', py: 1, minHeight: isPhone ? 0 : 120 }}>
        {groups.map((g) => (
          <Box key={g.key} sx={{ mb: 0.5 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, pt: 1, pb: 0.5 }}>
              <Typography variant="overline" sx={{ color: 'text.disabled', letterSpacing: 0.8, fontSize: 11 }}>
                {g.title}
              </Typography>
              {g.clearable && (
                <Typography
                  variant="caption"
                  sx={{ color: 'text.disabled', cursor: 'pointer', '&:hover': { color: 'text.secondary' } }}
                  onClick={() => {
                    clearRecent(user);
                    setRecent([]);
                  }}
                >
                  Clear
                </Typography>
              )}
            </Stack>
            {g.rows.map((row) => {
              rowIndex += 1;
              return renderRow(row, rowIndex);
            })}
          </Box>
        ))}

        {noResults && (
          <Box sx={{ px: 3, py: 2, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No records match “{q}”{filter ? ` in ${FILTERS.find((f) => f.key === filter)?.label}` : ''}.
            </Typography>
            {dexAvailable && (
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                Press {MOD_KEY} + Enter to ask Dex instead.
              </Typography>
            )}
          </Box>
        )}

        {!q && groups.length === 0 && (
          <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Type to search across your workspace.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Footer hints */}
      {!isPhone && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            px: 2.5,
            py: 1.1,
            borderTop: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
            bgcolor: alpha(theme.palette.grey[500], 0.04),
            color: 'text.disabled',
            '& .hint': { display: 'flex', alignItems: 'center', gap: 0.75, typography: 'caption' },
          }}
        >
          <Box className="hint">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> navigate
          </Box>
          <Box className="hint">
            <Kbd>↵</Kbd> open
          </Box>
          {q && (
            <Box className="hint">
              <Kbd>Tab</Kbd> filter
            </Box>
          )}
          {dexAvailable && (
            <Box className="hint">
              <Kbd>{MOD_KEY} ↵</Kbd> ask Dex
            </Box>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Box className="hint">
            <Kbd>Esc</Kbd> close
          </Box>
        </Stack>
      )}
    </Dialog>
  );
}
